import type { ReactNode } from 'react';

interface DockPanelProps {
  /** `standard` (up to 26rem × 16rem), `wide` (up to 38rem × 18rem, the city's Train grid) or `tall` (up to 40rem × 28rem, the Upgrades list and card); the viewport clamps them. */
  size?: 'standard' | 'wide' | 'tall';
  title: ReactNode;
  /** What the panel is about ("city", "building"): names the locate and close buttons. */
  kind: string;
  /** Level chip, right after the title. */
  level?: ReactNode;
  /** Further header chips (hp, fire, status). */
  chips?: ReactNode;
  /** Shows the ⌖ button when given. */
  onLocate?: () => void;
  onClose: () => void;
  /** Rows between the header and the tabs (stock line, worker focus). */
  toolbar?: ReactNode;
  tabs?: ReactNode;
  /** Pinned under the body; stacked, so give a row its own flex wrapper. */
  footer?: ReactNode;
  /** Scrolls inside the fixed-height panel, so the dock never changes size. */
  children: ReactNode;
}

const DOCK_CLASS = { standard: 'dock-standard', wide: 'dock-wide', tall: 'dock-tall' } as const;

/**
 * Shell for every bottom-centre dock panel: header (title, level, chips,
 * locate, close), optional tabs, a scrolling body and an optional action footer.
 */
export function DockPanel({
  size = 'standard',
  title,
  kind,
  level,
  chips,
  onLocate,
  onClose,
  toolbar,
  tabs,
  footer,
  children,
}: DockPanelProps) {
  return (
    <div className={`panel hud-dock ${DOCK_CLASS[size]} flex flex-col gap-1.5 p-3 text-xs`}>
      {/* One line, never wrapping, so nothing below moves as numbers change. */}
      <div className="flex shrink-0 items-center gap-2 overflow-hidden">
        <p className="panel-title min-w-0 truncate">{title}</p>
        {level}
        {chips}
        <span className="flex-1" />
        {onLocate && (
          <button
            type="button"
            aria-label={`Locate ${kind}`}
            title={`Locate ${kind}`}
            onClick={onLocate}
            className="btn btn-ghost btn-icon h-6 w-6 shrink-0 text-xs"
          >
            ⌖
          </button>
        )}
        <button
          type="button"
          aria-label={`Close ${kind} panel`}
          title={`Close ${kind} panel`}
          onClick={onClose}
          className="btn btn-ghost btn-icon h-6 w-6 shrink-0 text-xs"
        >
          ✕
        </button>
      </div>
      {toolbar}
      {tabs}
      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto">{children}</div>
      {footer && <div className="flex shrink-0 flex-col gap-1.5 empty:hidden">{footer}</div>}
    </div>
  );
}
